import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { adminApi, TProfile, TAdminUser, getToken, removeToken } from '../utils/adminApi';
import { decodeTokenRoles } from '../utils/authTokens';
import { startLogout } from '../utils/oidc';
import { LoginRedirect } from './UserAuthContext';
import { SkeletonPage } from '../components/loader/Skeleton';

// ─── Types ─────────────────────────────────────────────────────────────
interface TAuthState {
  user:        TProfile | null;
  permissions: string[];
  admins:      TAdminUser[];
  loading:     boolean;
  accessDenied: boolean;
}

interface TAuthContext extends TAuthState {
  logout:   () => Promise<void>;
  hasPermission: (key: string) => boolean;
}

// ─── Context ───────────────────────────────────────────────────────────
const AdminAuthContext = React.createContext<TAuthContext | null>(null);

// ─── Provider ──────────────────────────────────────────────────────────
export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<TAuthState>({
    user:        null,
    permissions: [],
    admins:      [],
    loading:     true,
    accessDenied: false,
  });

  // Проверяем токен при монтировании
  React.useEffect(() => {
    let isMounted = true;

    const token = getToken();
    // Грузим админ-данные ТОЛЬКО при наличии ролей в токене. Иначе admin-эндпоинты
    // вернут 403, а главное — здесь НЕЛЬЗЯ трогать токен: он общий с UserAuthContext
    // (тикеты/профиль обычного пользователя). Раньше catch вызывал removeToken() и
    // уносил сессию не-админа → 401 на /api/tickets/my.
    if (!token) {
      setState(s => ({ ...s, loading: false, accessDenied: false }));
      return;
    }

    if (decodeTokenRoles(token).length === 0) {
      setState(s => ({ ...s, user: null, permissions: [], admins: [], loading: false, accessDenied: true }));
      return;
    }

    Promise.all([adminApi.profile.get(), adminApi.admins.list().catch(() => [] as TAdminUser[])])
      .then(([profile, admins]) => {
        if (isMounted) setState({ user: profile, permissions: profile.permissions, admins, loading: false, accessDenied: false });
      })
      .catch((error: unknown) => {
        // НЕ чистим общий токен — это зона ответственности UserAuthContext.
        const status = typeof error === 'object' && error !== null && 'status' in error
          ? Number((error as { status?: unknown }).status)
          : undefined;
        if (isMounted) setState({ user: null, permissions: [], admins: [], loading: false, accessDenied: status === 403 });
      });

    return () => { isMounted = false; };
  }, []);

  // Выход — единый OIDC RP-initiated logout (завершает сессию и на провайдере).
  const logout = async () => {
    removeToken();
    setState({ user: null, permissions: [], admins: [], loading: false, accessDenied: false });
    await startLogout();
  };

  const hasPermission = (key: string) => state.permissions.includes(key);

  return (
    <AdminAuthContext.Provider value={{ ...state, logout, hasPermission }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// ─── Hook ──────────────────────────────────────────────────────────────
export function useAdminAuth(): TAuthContext {
  const ctx = React.useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used inside AdminAuthProvider');
  return ctx;
}

/** То же без падения вне провайдера: общим компонентам (PlayerRef) в тестах и вне админки */
export function useAdminAuthOptional(): TAuthContext | null {
  return React.useContext(AdminAuthContext);
}

// ─── Guard ─────────────────────────────────────────────────────────────
export function AdminAuthGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, loading, accessDenied } = useAdminAuth();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}${location.hash}`;

  // Скелет раздела проявляется, только если загрузка затянулась — без мелькания
  if (loading) return <SkeletonPage />;
  if (accessDenied) {
    return <Navigate to="/panel/access-denied" replace state={{ from: returnTo }} />;
  }

  if (!user) return <LoginRedirect guard="admin" />;
  return <>{children}</>;
}
